'use client';

import { usePathname } from 'next/navigation';
import { useState, useEffect, useRef } from 'react';
import brands from '@/app/_assets/styles/brands/brands.json';

interface Brand { id: string; name: string; themeColor: string }

const LABEL_PREFIX = 'Design Tokens ';
const SHOWCASE_DEFAULT: Brand = {
  id: 'default',
  name: 'Showcase',
  themeColor: '#000000',
};
const allBrands: Brand[] = [SHOWCASE_DEFAULT, ...brands];

// Per-path brand defaults — automatically applied on navigation so each
// demo page opens with the brand that fits its story best.
// Both default-locale and `/en/` slugs are listed so localised variants
// keep the mapping even if Typo3 ships a divergent EN slug.
const BRAND_BY_PATH: Partial<Record<string, string>> = {
  '/demos/lumen-school': 'sparks',
  '/demos/aurea-medical': 'wu-alumni',
  '/en/demos/lumen-school': 'sparks',
  '/en/demos/aurea-medical': 'wu-alumni',
};

export function BrandSwitcher() {
  const [active, setActive] = useState('default');
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const ref = useRef<HTMLDivElement>(null);

  // Path-driven preselection: re-run only on route change. `active` is
  // intentionally excluded — otherwise a manual switch would re-enter this
  // effect and immediately snap the selection back to the path default.
  useEffect(() => {
    if (!pathname) return;
    // Try the exact pathname first (covers explicitly listed `/en/...` slugs)
    // and fall back to the locale-stripped variant so any future locale
    // automatically inherits the default-locale mapping when slugs match.
    const normalizedPath = pathname.replace(/^\/[a-z]{2}(?=\/|$)/, '');
    const nextBrand =
      BRAND_BY_PATH[pathname] ??
      BRAND_BY_PATH[normalizedPath] ??
      SHOWCASE_DEFAULT.id;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- route-driven preselection (not derived: manual switches must persist), plus DOM sync below
    setActive(nextBrand);
    if (nextBrand === SHOWCASE_DEFAULT.id) {
      delete document.documentElement.dataset.brand;
    } else {
      document.documentElement.dataset.brand = nextBrand;
    }
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  if (!pathname.includes('/demos')) return null;

  const activeBrand =
    allBrands.find((b) => b.id === active) ?? SHOWCASE_DEFAULT;

  const switchBrand = (brandId: string) => {
    setActive(brandId);
    setOpen(false);
    if (brandId === 'default') {
      delete document.documentElement.dataset.brand;
    } else {
      document.documentElement.dataset.brand = brandId;
    }
  };

  return (
    <div
      ref={ref}
      data-open={open || undefined}
      className="group fixed bottom-6 left-1/2 z-50 -translate-x-1/2 max-sm:bottom-4"
    >
      {open && (
        <ul
          role="listbox"
          aria-label="Brand auswaehlen"
          className="absolute bottom-[calc(100%+0.5rem)] left-1/2 m-0 min-w-48 -translate-x-1/2 list-none rounded-2xl border border-white/10 bg-black/95 p-1.5 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.4)] backdrop-blur-xl max-sm:min-w-40"
        >
          <li>
            <button
              type="button"
              role="option"
              aria-selected={active === SHOWCASE_DEFAULT.id}
              data-active={active === SHOWCASE_DEFAULT.id || undefined}
              onClick={() => { switchBrand(SHOWCASE_DEFAULT.id); }}
              className="flex w-full cursor-pointer items-center gap-2.5 rounded-fab-bar-button border-0 bg-transparent px-3 py-2 text-left text-[0.8125rem] font-medium whitespace-nowrap text-white/70 transition-colors hover:bg-white/10 hover:text-white data-active:bg-white/12 data-active:text-white"
            >
              <span
                className="inline-block size-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: SHOWCASE_DEFAULT.themeColor }}
              />
              {LABEL_PREFIX}
              {SHOWCASE_DEFAULT.name}
            </button>
          </li>
          {brands.length > 0 && (
            <li
              role="separator"
              className="mx-2 my-1 h-px bg-white/10"
            />
          )}
          {brands.map((brand) => (
            <li key={brand.id}>
              <button
                type="button"
                role="option"
                aria-selected={active === brand.id}
                data-active={active === brand.id || undefined}
                onClick={() => { switchBrand(brand.id); }}
                className="flex w-full cursor-pointer items-center gap-2.5 rounded-fab-bar-button border-0 bg-transparent px-3 py-2 text-left text-[0.8125rem] font-medium whitespace-nowrap text-white/70 transition-colors hover:bg-white/10 hover:text-white data-active:bg-white/12 data-active:text-white"
              >
                <span
                  className="inline-block size-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: brand.themeColor }}
                />
                {LABEL_PREFIX}
                {brand.name}
              </button>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Brand wechseln. Aktuell: ${LABEL_PREFIX}${activeBrand.name}`}
        className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/10 bg-black/90 px-4 py-2.5 text-xs font-medium text-white shadow-[0_10px_30px_-5px_rgba(0,0,0,0.3)] backdrop-blur-xl transition [@media(hover:hover)]:hover:-translate-y-px hover:shadow-[0_15px_35px_-5px_rgba(0,0,0,0.4)] group-data-open:bg-white group-data-open:text-black max-sm:size-11 max-sm:justify-center max-sm:p-0"
      >
        <span
          className="inline-block size-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: activeBrand.themeColor }}
        />
        <span className="whitespace-nowrap max-sm:hidden">
          {LABEL_PREFIX}
          {activeBrand.name}
        </span>
      </button>
    </div>
  );
}
